/**
 * Cuentas por Pagar — list + aging.
 *
 * Every money figure is per-currency. MXN and USD are never summed: a border
 * distributor's dollar purchases would otherwise disappear into a peso total
 * that matches nothing the supplier says is owed.
 */
import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import {
  Badge, Button, Card, Table, TableBody, TableCell, TableHead,
  TableHeaderCell, TableRow, Text, Title,
} from '@tremor/react';
import { currentUser } from '../../api/auth';
import { fetchPayables, fetchPayablesAging } from '../../api/payables';
import type {
  ApAgingBucket, ApCurrencyTotal, ApInvoice, ApStatus, User,
} from '../../api/types';
import { payablesPerms } from '../../lib/payablesPerms';
import { t } from '../../lib/i18n';
import AccessDenied from '../../components/AccessDenied';
import Header from '../../components/Header';
import ErrorRetry from '../../components/ErrorRetry';
import PayableUploadModal from '../../components/PayableUploadModal';
import TableSkeleton from '../../components/TableSkeleton';

const BUCKETS = ['por_vencer', '1-30', '31-60', '61-90', '90+'] as const;

export function money(v: number, moneda: string): string {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: moneda || 'MXN',
    minimumFractionDigits: 2,
  }).format(v);
}

export function statusBadge(s: ApStatus) {
  const color =
    s === 'pagada' ? 'emerald'
      : s === 'parcial' ? 'amber'
        : s === 'cancelada' ? 'gray'
          : s === 'pendiente' ? 'orange' : 'blue';
  return <Badge color={color}>{t.payables.status[s]}</Badge>;
}

function PayablesInner() {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  const [invoices, setInvoices] = useState<ApInvoice[] | null>(null);
  const [summary, setSummary] = useState<ApCurrencyTotal[]>([]);
  const [aging, setAging] = useState<ApAgingBucket[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => { currentUser().then(setUser).catch(() => setUser(null)); }, []);

  const perms = payablesPerms(user);

  const load = () => {
    setErr(null);
    Promise.all([fetchPayables({}), fetchPayablesAging()])
      .then(([list, buckets]) => {
        setInvoices(list.invoices);
        setSummary(list.summary);
        setAging(buckets);
      })
      .catch((e: Error) => setErr(e.message));
  };

  useEffect(() => {
    if (perms.canView) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perms.canView]);

  if (user === undefined) return <TableSkeleton />;
  if (!user || !perms.canView) {
    return <AccessDenied user={user ?? ({ username: '', role: 'staff', sid_assigned: null } as User)} />;
  }

  const monedas = Array.from(new Set(aging.map((b) => b.moneda)));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Title>{t.payables.title}</Title>
        {perms.canCapture ? (
          <Button onClick={() => setUploading(true)}>{t.payables.upload}</Button>
        ) : null}
      </div>

      {/* Per-currency "por pagar" totals — deliberately separate cards so no
          one is tempted to read a blended number. */}
      <div className="flex flex-wrap gap-3">
        {summary.map((s) => (
          <Card key={s.moneda} className="max-w-xs">
            <Text>{t.payables.porPagar} · {s.moneda}</Text>
            <Title>{money(s.total, s.moneda)}</Title>
            <Text>{s.count} {t.common.results}</Text>
          </Card>
        ))}
      </div>

      {/* Aging strip, one row per currency. */}
      {monedas.map((moneda) => (
        <Card key={moneda}>
          <Text>{moneda}</Text>
          <div className="mt-2 grid grid-cols-5 gap-2 overflow-x-auto">
            {BUCKETS.map((b) => {
              const cell = aging.find((x) => x.moneda === moneda && x.bucket === b);
              return (
                <div key={b}>
                  <Text>{t.payables.buckets[b]}</Text>
                  <Text className="font-semibold">{money(cell?.total ?? 0, moneda)}</Text>
                </div>
              );
            })}
          </div>
        </Card>
      ))}

      {err ? <ErrorRetry message={err} onRetry={load} /> : null}
      {!err && invoices === null ? <TableSkeleton /> : null}
      {!err && invoices !== null && invoices.length === 0 ? (
        <Card><Text>{t.payables.empty}</Text></Card>
      ) : null}

      {!err && invoices !== null && invoices.length > 0 ? (
        <Card>
          <div className="overflow-x-auto">
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>{t.payables.folio}</TableHeaderCell>
                  <TableHeaderCell>{t.payables.proveedor}</TableHeaderCell>
                  <TableHeaderCell>{t.payables.fecha}</TableHeaderCell>
                  <TableHeaderCell>{t.payables.vence}</TableHeaderCell>
                  <TableHeaderCell>{t.payables.total}</TableHeaderCell>
                  <TableHeaderCell>{t.payables.saldo}</TableHeaderCell>
                  <TableHeaderCell>{t.payables.estado}</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices.map((inv) => (
                  <TableRow key={inv.id}>
                    <TableCell>
                      <Link to={`/pagar/${inv.id}`}>{inv.folio || inv.id.slice(0, 8)}</Link>
                      {inv.tipo_comprobante === 'E' ? (
                        <Badge color="violet" className="ml-2">{t.payables.credito}</Badge>
                      ) : null}
                    </TableCell>
                    <TableCell>{inv.emisor_nombre || inv.rfc}</TableCell>
                    <TableCell>{inv.fecha}</TableCell>
                    <TableCell>{inv.due_date}</TableCell>
                    <TableCell>{money(inv.total, inv.moneda)}</TableCell>
                    <TableCell>{money(inv.saldo, inv.moneda)}</TableCell>
                    <TableCell>{statusBadge(inv.status)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      ) : null}

      {uploading ? (
        <PayableUploadModal
          onClose={() => setUploading(false)}
          onDone={() => { setUploading(false); load(); }}
        />
      ) : null}
    </div>
  );
}

/**
 * Page shell. Every routed page in this app renders its OWN Header + <main>
 * (App.tsx has no shared authed layout), so without this the payables pages
 * had no nav bar at all — you could reach /pagar and not navigate away — and
 * sat flush against the window edges with no padding.
 */
export default function Payables() {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  useEffect(() => { currentUser().then(setUser).catch(() => setUser(null)); }, []);
  if (user === undefined) return null;
  if (!user) return <Navigate to="/login" replace />;
  return (
    <div className="min-h-screen flex flex-col">
      <Header user={user} />
      <main className="flex-1 px-6 py-6 space-y-4 max-w-screen-2xl mx-auto w-full">
        {payablesPerms(user).canView ? <PayablesInner /> : <AccessDenied user={user} />}
      </main>
    </div>
  );
}
